<template>
  <section class="h-full mx-4">
    <BaseTable
      v-if="divisions"
      :headers="tableHeaders"
      :list="divisions"
      table-id="divisions"
      @deleteMultiselect="onDeleteMultiselect"
      @add-new="onAddNew"
      @refresh-data="refreshData"
      clickable
      @table-item-clicked="onEdit"
      @apply-filters="onApplyFilters"
      @reset-filters="onResetFilters">
      <template #actions="{ item }">
        <div class="flex items-center justify-center gap-1">
          <ActionButton
            :isEnabled="item.isFromManager"
            color="red"
            :icon="IconTrashOutline"
            @action="onDeleteRow(item as Division)" />
        </div>
      </template>

      <template #filters>
        <section class="flex flex-col items-start justify-start w-full max-w-xl py-4 mx-auto">
          <div>
            <label class="space-y-2 text-sm tracking-wide text-gray-400 uppercase">{{
              $t('baseObject.isActive')
            }}</label>
            <BaseTriStateToggle
              :label="$t('baseObject.isActive')"
              name="isActive"
              v-model="filterIsActive" />
          </div>
        </section>
      </template>
    </BaseTable>

    <DivisionModalCreateEdit
      v-if="createEditModalActive"
      :division="division"
      :title="modalEditCreateTitle()"
      :mode="createEditModalMode"
      @close-modal="createEditModalActive = false"
      @save="onSave" />

    <BaseModalConfirm
      v-if="modalDeleteRow"
      @cancel="modalDeleteRow = false"
      @confirm="onDeleteSingle()" />
  </section>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { useToastStore } from '@/stores/toast';
import { v4 as uuidv4 } from 'uuid';
import { removeDivisionsByIds, getDivisions } from '@/services/api/documents';

import BaseTable from '@/components/common/BaseTable.vue';
import DivisionModalCreateEdit from '@/components/documents/DivisionModalCreateEdit.vue';
import BaseModalConfirm from '@/components/common/BaseModalConfirm.vue';
import IconTrashOutline from '@/assets/icons/IconTrashOutline.vue';
import ActionButton from '@/components/common/ActionButton.vue';
import BaseTriStateToggle from '@/components/common/BaseTriStateToggle.vue';

import { type Division, eDivision } from '@/types/Division';
import type { TableFilterData } from '@/types/Base';
import { filterActive, filterData, sortData } from '@/utils/table';
import { useI18n } from 'vue-i18n';
import { useLookupsStore } from '@/stores/lookups';

const { reloadLookups } = useLookupsStore();
const { t: $t } = useI18n({ useScope: 'global' });
const divisions = ref<Division[]>();

const filterIsActive = ref<boolean | undefined>(undefined);

const tableHeaders = [
  { value: 'code', text: $t('baseObject.code'), type: 'text', active: true },
  { value: 'name', text: $t('baseObject.name'), type: 'text', active: true },
  { value: 'originString', text: $t('salesperson.type'), type: 'text', active: true },
  { value: 'isFromManager', text: $t('baseObject.isFromManager'), type: 'check', active: true },
  { value: 'isActive', text: $t('baseObject.isActive'), type: 'check', active: true },
  { value: 'actions', text: $t('baseObject.actions'), active: true },
];

function initializeDivision(): Division {
  return {
    id: uuidv4(),
    code: '',
    name: '',
    origin: 0,
    originString: '',
    isFromManager: true,
    isActive: false,
  };
}

onMounted(async () => {
  const res = await getDivisions();
  divisions.value = populateDivisionField(res);
});

function getDivisionString(division: number): string {
  switch (division) {
    case eDivision.Sales:
      return 'Sales';
    case eDivision.Purchase:
      return 'Purchase';
    case eDivision.Inventory:
      return 'Inventory';
    case eDivision.Financial:
      return 'Financial';
    case eDivision.Marketing:
      return 'Marketing';
    case eDivision.Other:
      return 'Other';
    default:
      return 'Unknown';
  }
}

function populateDivisionField(divisions: Division[]): Division[] {
  return divisions.map((division) => ({
    ...division,
    originString: division.origin !== null ? getDivisionString(division.origin) : '',
  }));
}

const toastStore = useToastStore();

async function onDeleteMultiselect(items: Division[]) {
  const itemsArray = Array.isArray(items) ? items : [items];
  const itemsToDelete = itemsArray.filter((item) => item.isFromManager);
  if (itemsToDelete.length === 0) {
    toastStore.error({ text: $t('messages.info.selectedItemsCannotBeDeleted') });
    return;
  }

  const idsToDelete = itemsToDelete.map((item) => item.id);
  const res = await removeDivisionsByIds(idsToDelete);
  if (!res && divisions.value)
    divisions.value = divisions.value.filter((division) => !idsToDelete.includes(division.id));
  res
    ? toastStore.error({ text: res })
    : toastStore.success({ text: $t('messages.success.successfullyDeleted') });

  await reloadLookups(['divisions']);
}

async function onDeleteSingle() {
  modalDeleteRow.value = false;
  if (!deleteRowItem.value) return;
  const idToDelete = deleteRowItem.value.id;
  const res = await removeDivisionsByIds([idToDelete]);
  if (!res && divisions.value)
    divisions.value = divisions.value.filter((division) => division.id !== idToDelete);
  res
    ? toastStore.error({ text: res })
    : toastStore.success({ text: $t('messages.success.successfullyDeleted') });

  await reloadLookups(['divisions']);
}

const division = ref<Division>(initializeDivision());

const createEditModalActive = ref(false);
const createEditModalMode = ref<'add' | 'edit'>('add');

function onAddNew() {
  division.value = initializeDivision();
  createEditModalActive.value = true;
  createEditModalMode.value = 'add';
}

function onEdit(item: Division) {
  division.value = item;
  createEditModalMode.value = 'edit';
  createEditModalActive.value = true;
}

async function onSave(modifiedData: Division) {
  if (!divisions.value) return;

  const index = divisions.value.findIndex((item) => item.id === modifiedData.id);

  if (index !== -1) divisions.value[index] = modifiedData;
  else divisions.value.push(modifiedData);
  divisions.value = populateDivisionField(divisions.value);
  createEditModalActive.value = false;

  await reloadLookups(['divisions']);
}

async function refreshData(data: TableFilterData) {
  const response = await getDivisions();
  let processedData = populateDivisionField(response);

  if (data.query) {
    processedData = filterData(processedData, data.query, ['name', 'code', 'originString']);
  }

  if (data.column !== undefined && data.order) {
    processedData = sortData(processedData, data.column, data.order, tableHeaders);
  }

  if (data.isActive !== undefined) processedData = filterActive(processedData, data.isActive);

  divisions.value = processedData;
}

const modalDeleteRow = ref(false);
const deleteRowItem = ref<Division>();
function onDeleteRow(item: Division) {
  deleteRowItem.value = item;
  modalDeleteRow.value = true;
}

function modalEditCreateTitle() {
  if (division.value && !division.value.isFromManager)
    return `${$t('common.actions.view')} Division`;
  return createEditModalMode.value === 'edit'
    ? `${$t('common.actions.edit')} Division`
    : `${$t('common.actions.create')} Division`;
}

function onApplyFilters(data: TableFilterData) {
  data.isActive = filterIsActive.value ?? undefined;
  refreshData(data);
}

function onResetFilters() {
  filterIsActive.value = undefined;
  refreshData({});
}
</script>
